/**
 * @fileoverview Pure resolver: StepContent (+ flow-level default) → the
 * screen's animation plan, a Map of elementRef → PlanEntry.
 * @module screen-view-v2/animations/resolveScreenAnimationPlan
 *
 * Resolution priority (lowest → highest, later Map.set wins):
 *   1. Screen-level entrance distributed over present slots:
 *      content.screenAnimation.enterAnimation
 *        > content.animated === true (default fadeIn)
 *        > FlowDocument.defaultScreenAnimation
 *      (+ the hardcoded Carousel per-slide rows, owner D-F14 — see below).
 *      With a known kind a slot — and a slide row — takes a stagger step only if it
 *      actually renders (owner D-F14d), decided by the component's own predicate
 *      (utils/renderPredicates.ts).
 *   2. Per-entity fields (copy.animation, header.animation, actionBar.animation,
 *      featuredMedia.animation, options.itemAnimation/selection/deselection,
 *      options.items[N].animation)
 *   3. content.elementAnimations[] (exact dot-path elementRef)
 *
 * Element refs use schema dot-paths WITHOUT the `content.` prefix — the same
 * convention as editPath minus its `content.` root (`content.copy.title`
 * → `copy.title`). Special sub-keys use `#` (e.g. `options.item#selection`)
 * because they are event-driven, not mount-driven.
 *
 * Button animations (TapTargetStyle idle/tap/enter) are NOT in the plan —
 * Button reads its own `appearance` directly (see useElementAnimation.ts).
 */

import type {
  StepContent,
  ScreenAnimation,
  ElementAnimation,
  StaggerConfig,
} from '@segmently/schema-models/v2';
import type { SupportedTrigger } from './elementAnimationSpec';
import { normalizeSlideItems } from '../utils/normalizeCollections';
import {
  actionBarRenders,
  carouselShowsDetail,
  carouselShowsSubtitle,
  carouselShowsTitle,
  headerBarRenders,
  imageRenders,
  labelRenders,
  mediaRenders,
  paywallFooterRenders,
  paywallHeaderRenders,
} from '../utils/renderPredicates';

// ============================================================================
// Types
// ============================================================================

export interface PlanEntry {
  animation: ElementAnimation;
  stagger?: StaggerConfig | null;
  /** Fixed stagger index (screen-slot distribution). Per-item indexes come from render position. */
  staggerIndex?: number;
  defaultTrigger?: SupportedTrigger;
  forceLoop?: boolean;
}

export type ScreenAnimationPlan = Map<string, PlanEntry>;

export interface ResolvedScreenAnimationPlan {
  plan: ScreenAnimationPlan;
  /** True when at least one mount-driven (entrance) entry exists */
  hasEntranceEntries: boolean;
  /** options.items length — used to estimate total entrance duration */
  optionsItemCount: number;
  /** paywall.products.items length — same role as optionsItemCount */
  productItemCount: number;
}

/** Template key for per-item entrance (OptionsList.itemAnimation) */
export const OPTIONS_ITEM_REF = 'options.item';
export const OPTIONS_ITEM_SELECTION_REF = 'options.item#selection';
export const OPTIONS_ITEM_DESELECTION_REF = 'options.item#deselection';

/** Paywall product catalog refs (container slot + per-item template) */
export const PAYWALL_PRODUCTS_REF = 'paywall.products';
export const PAYWALL_PRODUCT_ITEM_REF = 'paywall.products.item';
export const PAYWALL_PRODUCT_ITEM_SELECTION_REF = 'paywall.products.item#selection';
export const PAYWALL_PRODUCT_ITEM_DESELECTION_REF = 'paywall.products.item#deselection';

/**
 * F14 slot refs (owner decision D-F14b). Stepper = the ProgressSteps progress bar + steps block
 * (`ProgressSteps` wrapper), field = TextInput's `Field`, Paywall header/footer = `PaywallHeader` /
 * `PaywallFooter` (their editPath is `content.paywall`, so the refs add `.header` / `.footer`).
 */
export const STEPPER_REF = 'stepper';
export const TEXT_FIELD_REF = 'textField';
export const PAYWALL_HEADER_REF = 'paywall.header';
export const PAYWALL_FOOTER_REF = 'paywall.footer';

/** Slide text rows enter with this preset, after the image (owner D-F14) */
const CAROUSEL_TEXT_PRESET = 'fadeInUp';

/**
 * Screen-entrance slot distribution order (visual top → bottom).
 * `copy.title`/`copy.subtitle` are targeted instead of the `copy` block because
 * Text is the universal mount point — HeroContent renders titles without a
 * CopyBlock wrapper. Both copy refs share ONE stagger slot. `paywall.products`
 * sits after `options` because the Paywall renders bullets above the catalog.
 */
const SCREEN_SLOTS = [
  'header', 'paywall.header', 'hero', 'featuredMedia', 'copy', 'options',
  'stepper', 'textField', 'paywall.products', 'actionBar', 'paywall.footer',
] as const;
type ScreenSlot = (typeof SCREEN_SLOTS)[number];

const LIST_SLOTS: readonly ScreenSlot[] = ['header', 'featuredMedia', 'copy', 'options', 'actionBar'];

/**
 * The slots each kind's screen component renders (D-F14d). Data for a slot a kind never renders
 * (a ProgressSteps template's `copy`, a Carousel's `header`) gets no entry and takes no step.
 * FlexibleLayout renders its header bar / action bar standalone or as a Header / Footer section —
 * both on `content.header` / `content.actionBar`.
 */
const KIND_SLOTS: Readonly<Record<string, readonly ScreenSlot[]>> = {
  HeroContent: ['header', 'hero', 'copy', 'actionBar'],
  HeroList: ['header', 'hero', 'copy', 'options', 'actionBar'],
  ListSinglePick: LIST_SLOTS,
  ListMultiPick: LIST_SLOTS,
  GridSinglePick: LIST_SLOTS,
  GridMultiPick: LIST_SLOTS,
  WheelPicker: ['header', 'featuredMedia', 'copy', 'actionBar'],
  TextInput: ['header', 'copy', 'textField', 'actionBar'],
  ProgressSteps: ['header', 'stepper'],
  Paywall: ['paywall.header', 'featuredMedia', 'copy', 'options', 'paywall.products', 'paywall.footer'],
  FlexibleLayout: ['header', 'actionBar'],
  WebEmbed: ['header', 'actionBar'],
  Carousel: [],
};

/** No / unknown kind (direct resolver calls): the pre-F14 slots, counted by data presence */
const LEGACY_SLOTS: readonly ScreenSlot[] = ['header', 'hero', 'featuredMedia', 'copy', 'options', 'paywall.products', 'actionBar'];

// ============================================================================
// Internals
// ============================================================================

const FADE_IN_DEFAULT: ElementAnimation = { preset: 'fadeIn' } as ElementAnimation;

function resolveScreenEnterSource(
  content: StepContent,
  defaultScreenAnimation: ScreenAnimation | null | undefined,
): { enterAnimation: ElementAnimation; stagger?: StaggerConfig | null } | null {
  const screenAnimation = content.screenAnimation;
  if (screenAnimation?.enterAnimation) {
    return { enterAnimation: screenAnimation.enterAnimation, stagger: screenAnimation.stagger };
  }
  if (content.animated === true) {
    return { enterAnimation: FADE_IN_DEFAULT, stagger: screenAnimation?.stagger };
  }
  if (defaultScreenAnimation?.enterAnimation) {
    return {
      enterAnimation: defaultScreenAnimation.enterAnimation,
      stagger: defaultScreenAnimation.stagger,
    };
  }
  return null;
}

function stripContentPrefix(ref: string): string {
  return ref.startsWith('content.') ? ref.slice('content.'.length) : ref;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * @param screenType the screen's StepKind. The F14 slots and the Carousel rows apply to their own
 *   kind only, and with a known kind a slot takes a stagger step only if it renders (D-F14d).
 *   Without a known kind the plan keeps the pre-F14 data-presence rule.
 * @param locale the render locale — text and images "render" when they resolve for it (the same
 *   resolution Text / Image use). Template variables are not applied: the plan is per screen.
 */
export function resolveScreenAnimationPlan(
  content: StepContent | null | undefined,
  defaultScreenAnimation?: ScreenAnimation | null,
  screenType?: string,
  locale?: string,
): ResolvedScreenAnimationPlan {
  const plan: ScreenAnimationPlan = new Map();
  if (!content) {
    return { plan, hasEntranceEntries: false, optionsItemCount: 0, productItemCount: 0 };
  }

  const options = content.options;
  const optionsItemCount = options?.items?.length ?? 0;
  const paywall = (content as { paywall?: Record<string, unknown> & { products?: { items?: unknown[] } } }).paywall;
  const paywallProducts = paywall?.products;
  const productItemCount = paywallProducts?.items?.length ?? 0;
  // ProductCatalog has no itemAnimation schema field — a per-item template can
  // only come from elementAnimations[]. Mirror the options double-animation
  // guard: items animating individually → skip the container slot.
  const hasProductItemTemplate = content.elementAnimations?.some(
    (target) => target?.animation && target.elementRef
      && stripContentPrefix(target.elementRef) === PAYWALL_PRODUCT_ITEM_REF,
  ) ?? false;

  // ── 1. Screen-level entrance distributed over present slots ──
  const screenSource = resolveScreenEnterSource(content, defaultScreenAnimation);
  if (screenSource) {
    const loc = locale ?? '';
    const kindSlots = screenType ? KIND_SLOTS[screenType] : undefined;
    const slots = kindSlots ?? LEGACY_SLOTS;
    // Candidates: the slots this kind renders whose data is present
    const present: ScreenSlot[] = SCREEN_SLOTS.filter((slot) => {
      if (!slots.includes(slot)) return false;
      switch (slot) {
        case 'header': return content.header != null;
        case 'paywall.header': return paywallHeaderRenders(paywall?.closeButton, paywall?.restoreLabel);
        case 'hero': return content.hero != null;
        case 'featuredMedia': return content.featuredMedia != null;
        case 'copy': return content.copy?.title != null || content.copy?.subtitle != null;
        // When itemAnimation exists the items animate individually — skip the
        // container so the list doesn't visibly animate twice.
        case 'options': return optionsItemCount > 0 && options?.itemAnimation == null;
        case 'stepper': return content.stepper != null;
        case 'textField': return content.textField != null;
        case 'paywall.products': return productItemCount > 0 && !hasProductItemTemplate;
        case 'actionBar': return content.actionBar != null;
        case 'paywall.footer':
          return paywallFooterRenders(paywall?.purchaseAction, paywall?.autoRenewLabel, paywall?.legalLinks);
        default: return false;
      }
    });
    // D-F14d: with a known kind, a candidate takes a step only if its component renders it. The
    // other candidates (OptionsList / BulletList, ProductCatalog, ProgressBar, Field,
    // PaywallHeader / PaywallFooter) render whenever their data is present.
    const renders = (slot: ScreenSlot): boolean => {
      if (!kindSlots) return true;
      switch (slot) {
        case 'header': return headerBarRenders(content.header);
        case 'hero': return imageRenders(content.hero, loc);
        case 'featuredMedia': return mediaRenders(content.featuredMedia);
        case 'copy': return labelRenders(content.copy?.title, loc) || labelRenders(content.copy?.subtitle, loc);
        case 'actionBar': return actionBarRenders(content.actionBar);
        default: return true;
      }
    };
    let rendered = 0;
    for (const slot of present) {
      // a candidate that renders nothing keeps an entry (no element, no effect) but takes no step
      const entry: PlanEntry = {
        animation: screenSource.enterAnimation,
        stagger: screenSource.stagger,
        staggerIndex: rendered,
      };
      if (slot === 'copy') {
        if (content.copy?.title != null) plan.set('copy.title', entry);
        if (content.copy?.subtitle != null) plan.set('copy.subtitle', entry);
      } else {
        plan.set(slot, entry);
      }
      if (renders(slot)) rendered += 1;
    }

    // Carousel (owner D-F14, hardcoded): per slide, the image enters with the screen's entrance,
    // then title → subtitle → detail with fadeInUp and the same timing, one stagger step apart —
    // the screen's step, else the runtime default (an empty StaggerConfig → DEFAULT_DELAY_PER_ITEM_MS).
    // D-F14d: each slide counts only the rows SliderItem renders (a missing image or an empty title
    // leaves no gap). Exact refs `carousel.items[N].content.<part>` (the Slider's normalized index),
    // so an elementAnimations[] entry for one slide still wins (layer 3). Inactive slides are
    // display:none, so the entrance replays each time a slide is shown.
    if (screenType === 'Carousel' && content.carousel != null) {
      const stagger = screenSource.stagger ?? {};
      const text = { ...screenSource.enterAnimation, preset: CAROUSEL_TEXT_PRESET } as ElementAnimation;
      const kind = content.carousel.kind;
      normalizeSlideItems(content.carousel.items).forEach((slide, index) => {
        const part = slide.content;
        const detail = (part as { detail?: Parameters<typeof labelRenders>[0] } | undefined)?.detail;
        const rows: Array<[string, ElementAnimation, boolean]> = [
          ['image', screenSource.enterAnimation, imageRenders(part?.image, loc)],
          ['title', text, carouselShowsTitle(kind) && labelRenders(part?.title, loc)],
          ['subtitle', text, carouselShowsSubtitle(kind) && labelRenders(part?.subtitle, loc)],
          ['detail', text, carouselShowsDetail(kind) && labelRenders(detail, loc)],
        ];
        let slideRendered = 0;
        for (const [name, animation, rowRenders] of rows) {
          plan.set(`carousel.items[${index}].content.${name}`, { animation, stagger, staggerIndex: slideRendered });
          if (rowRenders) slideRendered += 1;
        }
      });
    }
  }

  // ── 2. Per-entity fields ─────────────────────────────────────
  if (content.copy?.animation) {
    plan.set('copy', { animation: content.copy.animation, stagger: content.copy.stagger });
  }
  if (content.header?.animation) {
    plan.set('header', { animation: content.header.animation });
  }
  if (content.actionBar?.animation) {
    plan.set('actionBar', { animation: content.actionBar.animation });
  }
  if (content.featuredMedia?.animation) {
    plan.set('featuredMedia', { animation: content.featuredMedia.animation });
  }
  if (options?.itemAnimation) {
    plan.set(OPTIONS_ITEM_REF, {
      animation: options.itemAnimation,
      stagger: options.itemStagger,
    });
  }
  if (options?.selectionAnimation) {
    plan.set(OPTIONS_ITEM_SELECTION_REF, {
      animation: options.selectionAnimation,
      defaultTrigger: 'onSelectionChange',
    });
  }
  if (options?.deselectionAnimation) {
    plan.set(OPTIONS_ITEM_DESELECTION_REF, {
      animation: options.deselectionAnimation,
      defaultTrigger: 'onSelectionChange',
    });
  }
  options?.items?.forEach((item, index) => {
    if (item.animation) {
      plan.set(`options.items.${index}`, {
        animation: item.animation,
        stagger: options.itemStagger,
      });
    }
  });

  // ── 3. elementAnimations[] — highest priority ────────────────
  content.elementAnimations?.forEach((target) => {
    if (!target?.elementRef || !target.animation) return;
    plan.set(stripContentPrefix(target.elementRef), {
      animation: target.animation,
      stagger: target.stagger,
    });
  });

  let hasEntranceEntries = false;
  for (const [ref, entry] of plan) {
    const isEventDriven = ref.includes('#') || entry.defaultTrigger === 'onSelectionChange';
    // disabled entries (per-slot "None") never play — an all-None screen must
    // not arm the editor Play/replay window for nothing
    if (!isEventDriven && entry.animation?.disabled !== true) {
      hasEntranceEntries = true;
      break;
    }
  }

  return { plan, hasEntranceEntries, optionsItemCount, productItemCount };
}
